import { useMemo } from "react";

import { useEppoFeatureFlagsProvider } from "app/providers/CustomEppoProvider";
import { EppoFeatureFlagsWithJson } from "app/types/flags";

export type StockChangeGuardrailsFlag = {
  stock_correction?: {
    quantity_threshold?: number;
    value_threshold_eur?: number;
  };
  inbounding?: {
    deviation_threshold_percent?: number;
  };
  quantity_change_limit?: number;
};

export type ParsedGuardrails = {
  quantityThreshold?: number;
  valueThresholds?: number;
  deviationThreshold?: number;
  quantityChangeLimit?: number;
};

const QUANTITY_THRESHOLD_FALLBACK = 17;
const VALUE_THRESHOLD_EUR_FALLBACK = 35;
const DEVIATION_THRESHOLD_PERCENT_FALLBACK = 50;
const DEVIATION_THRESHOLD_QUANTITY_CHANGE_LIMIT = 5000;

const FALLBACK_GUARDRAILS: ParsedGuardrails = {
  quantityThreshold: QUANTITY_THRESHOLD_FALLBACK,
  valueThresholds: VALUE_THRESHOLD_EUR_FALLBACK,
  deviationThreshold: DEVIATION_THRESHOLD_PERCENT_FALLBACK,
  quantityChangeLimit: DEVIATION_THRESHOLD_QUANTITY_CHANGE_LIMIT,
};

export const useGuardrailsFeatureFlag = (): ParsedGuardrails | null => {
  const { jsonFlags, isJsonError } = useEppoFeatureFlagsProvider();
  return useMemo(() => {
    // Eppo could not be reached: keep guardrails active with the fallback values
    if (!jsonFlags) return isJsonError ? FALLBACK_GUARDRAILS : null;

    const guardrails = jsonFlags[
      EppoFeatureFlagsWithJson.STOCK_CHANGE_GUARDRAILS
    ] as StockChangeGuardrailsFlag | null;

    // Explicitly disabled
    if (guardrails && Object.keys(guardrails).length === 0) return null;
    return {
      quantityThreshold:
        guardrails?.stock_correction?.quantity_threshold ?? QUANTITY_THRESHOLD_FALLBACK,
      valueThresholds:
        guardrails?.stock_correction?.value_threshold_eur ?? VALUE_THRESHOLD_EUR_FALLBACK,
      deviationThreshold:
        guardrails?.inbounding?.deviation_threshold_percent ?? DEVIATION_THRESHOLD_PERCENT_FALLBACK,
      quantityChangeLimit:
        guardrails?.quantity_change_limit ?? DEVIATION_THRESHOLD_QUANTITY_CHANGE_LIMIT,
    };
  }, [isJsonError, jsonFlags]);
};
